import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import {
  LEARNING_HUB_ANCHOR_LINK_CLASS,
  LEARNING_HUB_ANCHOR_TARGET_PREFIX,
  LEARNING_HUB_IMAGE_PLACEHOLDER_CLASS,
  LEARNING_HUB_INTERNAL_LINK_CLASS,
  LEARNING_HUB_MISSING_IMAGE_CLASS,
  LEARNING_HUB_MODULE_PATH
} from '../../definitions/learning-hub.constants';
import {
  LearningHubArticle,
  LearningHubArticleMeta,
  LearningHubBreadcrumb,
  LearningHubCategories,
  LearningHubCategory,
  LearningHubIndex,
  LearningHubLanguage,
  LearningHubLanguageOption
} from '../../definitions/learning-hub.models';
import { LearningHubService } from '../../services/learning-hub.service';
import {
  categoryCommands,
  categoryTrail,
  isLearningHubLanguage,
  languageOptions,
  primaryCategory,
  resolveLanguage,
  routerUrlFromHref
} from '../../utils/learning-hub.utils';

type DetailView =
  | {
    status: 'ready';
    meta: LearningHubArticleMeta;
    article: LearningHubArticle;
    tree: LearningHubCategories | null;
    index: LearningHubArticleMeta[];
  }
  | { status: 'not-found' | 'error'; lang: LearningHubLanguage };

@Component({
  selector: 'app-learning-hub-article-detail',
  templateUrl: './article-detail.component.html',
  styleUrls: ['./article-detail.component.scss'],
  // Styles must reach the HTML injected with [innerHTML]; every rule is scoped under .lh-article.
  encapsulation: ViewEncapsulation.None
})
export class ArticleDetailComponent implements OnInit, OnDestroy {
  @ViewChild('content') contentRef: ElementRef<HTMLElement>;

  readonly modulePath = LEARNING_HUB_MODULE_PATH;
  loading = true;
  status: 'ready' | 'not-found' | 'error' = 'ready';
  lang: LearningHubLanguage;
  meta: LearningHubArticleMeta;
  article: LearningHubArticle;
  options: LearningHubLanguageOption[] = [];
  tree: LearningHubCategories | null = null;
  index: LearningHubArticleMeta[] = [];
  category: LearningHubCategory | null = null;
  breadcrumb: LearningHubBreadcrumb[] = [];

  private readonly DESTROY$ = new Subject<void>();

  constructor(
    private readonly _route: ActivatedRoute,
    private readonly _router: Router,
    private readonly _translate: TranslateService,
    private readonly _learningHubService: LearningHubService,
    private readonly _changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this._route.paramMap
      .pipe(
        tap(() => (this.loading = true)),
        switchMap((params: ParamMap) => this._load(params.get('lang'), params.get('slug'))),
        takeUntil(this.DESTROY$)
      )
      .subscribe((view: DetailView | null) => {
        if (!view) {
          return; // Redirected to the canonical /:lang/:slug URL.
        }
        this.loading = false;
        this.status = view.status;
        if (view.status === 'ready') {
          this.lang = view.meta.lang;
          this.meta = view.meta;
          this.article = view.article;
          this.options = languageOptions(view.meta);
          this.tree = view.tree;
          this.index = view.index;
          this.category = view.tree ? primaryCategory(view.meta, view.tree.categories) : null;
          this.breadcrumb = this._breadcrumb(view.meta, view.tree);
        } else {
          this.lang = view.lang;
          this.options = [];
          this.tree = null;
          this.category = null;
          this.breadcrumb = this._breadcrumb(null, null);
        }
        this._changeDetector.detectChanges();
        this._enhanceContent();
      });
  }

  ngOnDestroy(): void {
    this.DESTROY$.next();
    this.DESTROY$.complete();
  }

  changeLanguage(option: LearningHubLanguageOption): void {
    this._router.navigate(['/', this.modulePath, option.lang, option.slug]);
  }

  /** Internal links and in-page anchors inside the article HTML are handled by the router / scrolling. */
  onContentClick(event: MouseEvent): void {
    const anchor = (event.target as HTMLElement)?.closest?.('a') as HTMLAnchorElement | null;
    if (!anchor || event.ctrlKey || event.metaKey || event.shiftKey || event.button > 0) {
      return;
    }
    const href = anchor.getAttribute('href') || '';
    if (anchor.classList.contains(LEARNING_HUB_INTERNAL_LINK_CLASS)) {
      event.preventDefault();
      this._router.navigateByUrl(routerUrlFromHref(href));
    } else if (anchor.classList.contains(LEARNING_HUB_ANCHOR_LINK_CLASS)) {
      event.preventDefault();
      const target = this.contentRef?.nativeElement.querySelector(`.${LEARNING_HUB_ANCHOR_TARGET_PREFIX}${href.substring(1)}`);
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /** Learning Hub › Section › Subsection › article title. */
  private _breadcrumb(
    meta: LearningHubArticleMeta | null,
    tree: LearningHubCategories | null
  ): LearningHubBreadcrumb[] {
    const lang = meta?.lang || this.lang;
    const home: LearningHubBreadcrumb = {
      label: 'learningHub.title',
      translate: true,
      link: ['/', this.modulePath],
      queryParams: { lang }
    };
    if (!meta) {
      return [home];
    }
    const trail = tree && this.category ? categoryTrail(this.category.key, tree.categories) : [];
    return [
      home,
      ...trail.map((category: LearningHubCategory) => ({
        label: `learningHub.categories.${category.key}`,
        translate: true,
        link: categoryCommands(category.key, tree.categories),
        queryParams: { lang }
      })),
      { label: meta.title }
    ];
  }

  private _load(langParam: string | null, slug: string): Observable<DetailView | null> {
    const preferred = resolveLanguage(langParam, this._translate.currentLang, this._translate.defaultLang);
    if (langParam !== null && !isLearningHubLanguage(langParam)) {
      return of({ status: 'not-found', lang: preferred } as DetailView);
    }
    const lookup$ =
      langParam === null
        ? this._learningHubService.findBySlugInAnyLanguage(slug, preferred)
        : this._learningHubService.findBySlug(preferred, slug);

    return lookup$.pipe(
      switchMap((meta: LearningHubArticleMeta | null) => {
        if (!meta) {
          return of({ status: 'not-found', lang: preferred } as DetailView);
        }
        if (langParam === null) {
          // Legacy URL without language (/learning-hub/:slug): move to the canonical URL.
          this._router.navigate(['/', this.modulePath, meta.lang, meta.slug], { replaceUrl: true });
          return of(null);
        }
        // The tree only adds the breadcrumb and the side column: the article is shown even if it fails.
        return combineLatest([
          this._learningHubService.getArticle(meta.id),
          this._learningHubService.getCategories().pipe(catchError(() => of(null))),
          this._learningHubService.getIndex(meta.lang)
        ]).pipe(
          map(
            ([article, tree, index]: [LearningHubArticle, LearningHubCategories | null, LearningHubIndex]) =>
              ({ status: 'ready', meta, article, tree, index: index.articles } as DetailView)
          )
        );
      }),
      catchError(() => of({ status: 'error', lang: preferred } as DetailView))
    );
  }

  /** Lazy images and a visible placeholder (with the alt text) when an image cannot be loaded. */
  private _enhanceContent(): void {
    const container = this.contentRef?.nativeElement;
    if (!container) {
      return;
    }
    const unavailable = this._translate.instant('learningHub.imageUnavailable');
    Array.from(container.querySelectorAll(`.${LEARNING_HUB_IMAGE_PLACEHOLDER_CLASS}`)).forEach((placeholder: Element) => {
      placeholder.textContent = placeholder.textContent || unavailable;
    });
    Array.from(container.querySelectorAll('img')).forEach((image: HTMLImageElement) => {
      image.setAttribute('loading', 'lazy');
      const markMissing = (): void => {
        image.classList.add(LEARNING_HUB_MISSING_IMAGE_CLASS);
        const placeholder = document.createElement('span');
        placeholder.className = LEARNING_HUB_IMAGE_PLACEHOLDER_CLASS;
        placeholder.textContent = image.getAttribute('alt') || unavailable;
        image.replaceWith(placeholder);
      };
      // Listeners are attached in the same task that inserted the HTML, before any load error can fire.
      image.addEventListener('error', markMissing, { once: true });
    });
  }
}
